import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { environment } from '@environments/environment';
import { ApiResponse } from '@app/_models';
import { retry, catchError } from 'rxjs/operators';
import { Observable, throwError } from 'rxjs';
import { AlertService } from '@app/_services';
import { Picture } from './picture';
import { SpringQuery } from '@app/_helpers/spring-query';
import { SharedService } from './../_shared/shared.service';

@Injectable({ providedIn: 'root' })
export class PictureService {

	statuses: any = [
		{ value: '0', name: 'Active' },
		{ value: '2', name: 'Suspended' },
		{ value: '1', name: 'Canceled' }
	];

	mediaTypes = [
		{ name: 'Image', value: 1 },
		{ name: 'Movie', value: 2 }
	];


	constructor
		(
			private http: HttpClient,
			private alertService: AlertService,
			private sharedService: SharedService
		) { }

	urlEndpoint = 'pictures';

	add(picture: Picture): Observable<ApiResponse> {
		console.log('posting... ' + JSON.stringify(picture));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, picture);
	}

	update(picture: Picture): Observable<ApiResponse> {
		return this.http.patch<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, picture);
	}

	delete(developmentId: number): Observable<ApiResponse> {
		return this.http.delete<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${developmentId}`);
	}

	findAll(): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	findByFilter(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta: any, globalFilter: any, filters: any, springQuery: SpringQuery): Observable<ApiResponse> {
		let params = this.prepareParamsFromPrimeNgDataTable(page, size, sortBy, sortOrder, multiSortMeta, globalFilter, filters, springQuery);
		console.log('posting... ' + JSON.stringify(filters));
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: params }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findBySpringQuery(springQuery: SpringQuery): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}`, { params: springQuery.getParams() }).pipe(retry({ count: 2, delay: 1500 }));
	}

	findById(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}`).pipe(retry({ count: 2, delay: 1500 }));
	}

	audit(id: number): Observable<ApiResponse> {
		return this.http.get<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/${id}/audit`).pipe(retry({ count: 2, delay: 1500 }));
	}

	sortPictureOrder(pictures: Picture[]): Observable<ApiResponse> {
		// No retry() here — this is a write (sort order update). Retrying a write can
		// result in a stale response overwriting a more-recent reorder that completed first.
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/sort`, pictures);
	}

	removePicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove`, data);
	}
	
	removeMultiplePicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove-multiple-pictures`, data);
	}

	removeBlogPicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove-blog-pictures`, data);
	}
	
	removeMultipleBlogPicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove-multiple-blog-pictures`, data);
	}

	removelandingPageCMSPicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove-landing-page-cms-pictures`, data);
	}
	
	removeMultiplelandingPageCMSPicture(data): Observable<ApiResponse> {
		// addTaskTags allows multiple tags of same tagType for each task (ie users #4)
		console.log('posting... ' + JSON.stringify(data));
		return this.http.post<ApiResponse>(`${environment.apiUrl}/${this.urlEndpoint}/remove-multiple-landing-page-cms-pictures`, data);
	}
	
	getLocations(): Observable<ApiResponse> {
		return this.http.post<ApiResponse>(`${environment.apiUrl}/locations`, {});
	}

	uploadFile(
		file: any,
		shortTermRentalId: any,
		developmentId: any,
		mediaType: number,
		describesType?: string
	) {
		const formData: FormData = new FormData();

		// file
		formData.append('file', file, file.name);

		// mediaType (1 = image, 2 = video)
		formData.append('mediaType', mediaType.toString());

		// describesType — required by backend for STR uploads; caller may override but
		// backend defaults to "EP" (Entire Property) when not supplied.
		if (describesType) {
			formData.append('describesType', describesType);
		}

		// additional IDs
		if (developmentId) {
			formData.append('developmentId', developmentId);
		}

		if (shortTermRentalId) {
			formData.append('shortTermRentalId', shortTermRentalId);
		}

		return this.http.post(
			`${environment.apiUrl}/${this.urlEndpoint}/upload`,
			formData
		);
	}


	  uploadFileActivity(file: any, activityId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (activityId) {
			formData.append('activityId', activityId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/upload`, formData);
	  }

	  uploadFileActivityFee(file: any, activityFeeId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (activityFeeId) {
			formData.append('activityFeeId', activityFeeId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/upload`, formData);
	  }

	  uploadFileActivitySchedule(file: any, activityScheduleId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (activityScheduleId) {
			formData.append('activityScheduleId', activityScheduleId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/upload`, formData);
	  }

	  uploadFileForlandingPageCMS(file: any, landingPageCMSId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (landingPageCMSId) {
			formData.append('landingPageCMSId', landingPageCMSId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/landing-page-cms/upload`, formData);
	  }

	  uploadFileForBlog(file: any, blogId: any) {
	    const formData: FormData = new FormData();
	    formData.append('file', file, file.name);
		if (blogId) {
			formData.append('blogId', blogId);
		}
	     //append any other key here if required
	    return this.http.post(`${environment.apiUrl}/${this.urlEndpoint}/blog/upload`, formData);
	  }
	

	prepareParamsFromPrimeNgDataTable(page: any, size: any, sortBy: any, sortOrder: any, multiSortMeta, globalFilter: any, filters: any, springQuery: SpringQuery) {
		try {
			console.log(
				'  |  page:' + page
				+ '  |  size:' + size
				+ '  |  sortBy:' + sortBy
				+ '  |  sortOrder:' + sortOrder
				+ '  |  multiSortMeta:' + JSON.stringify(multiSortMeta)
				+ '  |  globalFilter:' + globalFilter
				+ '  |  filters:' + JSON.stringify(filters)
				+ '  |  springQuery:' + JSON.stringify(springQuery)
			);

			let params = new HttpParams();
			params = springQuery.getParams();
			if (!isNaN(page)) { params = params.append('page', page); }
			if (!isNaN(size)) { params = params.append('size', size); }
			if (sortBy) { params = params.append('sort', sortBy + ',' + (sortOrder === 1 ? 'asc' : 'desc')); }
			if (multiSortMeta) {
				// if sortMode="multiple" in primeng table then multiSortMeta is used for ALL sorting (even just one column.)
				// Otherwise, single sorting is used above.
				// event.multiSortMeta:[{"field":"lastName","order":1},{"field":"firstName","order":1}]
				for (let i = 0; i < multiSortMeta.length; i++) {
					let json = JSON.parse(JSON.stringify(multiSortMeta[i]));
					params = params.append('sort', json.field + ',' + (json.order === 1 ? 'asc' : 'desc'));
				}
			}
			// globalFilter works, but did not implemnt in Spring but not hard to write custom findAll and specify certain columns to look.
			if (globalFilter) { params = params.append('filter', globalFilter); }

			// multiselect or input or whatever... these work slick.
			if (filters) {
				// key:status val:{"value":["A"],"matchMode":"in"}
				for (let i = 0, keys = Object.keys(filters), ii = keys.length; i < ii; i++) {
					console.log('key:' + keys[i] + ' val:' + JSON.stringify(filters[keys[i]]));
					params = params.append(keys[i], filters[keys[i]].value);
				}
			}
			return params;
		} catch (error) {
			console.log('prepareParamsFromPrimeNgDataTable()' + error);
		}
	}
}
